import { Circle, type Container, type Graphics, Rectangle } from "pixi.js";
import { memo, useCallback, useMemo, useRef } from "react";
import { useConstantScreenSize } from "../hooks/useConstantScreenSize";
import { useMapObjectInteractions } from "../hooks/useMapObjectInteractions";
import { useScene } from "../hooks/useScene";
import { ACTIVE_INTERACTION_COLOR } from "../interaction";
import type { LayerId, MapItemHandler, MeasurementMapObject } from "../types";

export const MEASUREMENT_RADIUS = 6;
const MEASUREMENT_HIT_RADIUS = 12;
export const MEASUREMENT_CULL_RADIUS = MEASUREMENT_HIT_RADIUS;
export const MEASUREMENT_SCALE_LIMITS = {
    minScale: 0.1,
    maxScale: 0.5,
};
interface MeasurementObjectProps {
    item: MeasurementMapObject;
    layerId: LayerId;
    onItemClick?: MapItemHandler<MeasurementMapObject>;
    onItemHover?: MapItemHandler<MeasurementMapObject>;
    onItemLeave?: MapItemHandler<MeasurementMapObject>;
}

const getMeasurementColor = (hovered: boolean) => {
    if (hovered) return ACTIVE_INTERACTION_COLOR;
    return 0xff0000;
};

const MeasurementObject = ({
    item,
    layerId,
    onItemClick,
    onItemHover,
    onItemLeave,
}: MeasurementObjectProps) => {
    const { coordinates } = useScene();
    const containerRef = useRef<Container | null>(null);
    const graphicRef = useRef<Graphics | null>(null);

    const measurementPixi = useMemo(
        () => coordinates.worldToScene({ x: item.x, y: item.y }),
        [coordinates, item.x, item.y],
    );

    const hitArea = useMemo(() => new Circle(0, 0, MEASUREMENT_HIT_RADIUS), []);
    const cullArea = useMemo(
        () =>
            new Rectangle(
                -MEASUREMENT_CULL_RADIUS,
                -MEASUREMENT_CULL_RADIUS,
                MEASUREMENT_CULL_RADIUS * 2,
                MEASUREMENT_CULL_RADIUS * 2,
            ),
        [],
    );

    useConstantScreenSize(containerRef, MEASUREMENT_SCALE_LIMITS);

    const interaction = useMapObjectInteractions({
        item,
        layerId,
        onItemClick,
        onItemHover,
        onItemLeave,
    });

    const draw = useCallback(
        (graphics: Graphics) => {
            graphicRef.current = graphics;
            graphics.clear();
            graphics.circle(0, 0, MEASUREMENT_RADIUS);
            graphics.fill(getMeasurementColor(interaction.hovered));
        },
        [interaction.hovered],
    );

    return (
        <pixiContainer
            ref={containerRef}
            x={measurementPixi.x}
            y={measurementPixi.y}
            label="measurement-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={cullArea}
        >
            <pixiGraphics
                ref={graphicRef}
                eventMode={interaction.eventMode}
                cursor={interaction.cursor}
                hitArea={hitArea}
                onPointerOver={interaction.onPointerOver}
                onPointerOut={interaction.onPointerOut}
                onPointerTap={interaction.onPointerTap}
                draw={draw}
            />
        </pixiContainer>
    );
};

export const MeasurementSelectionOverlay = memo(
    ({ item }: { item: MeasurementMapObject }) => {
        const { coordinates } = useScene();
        const containerRef = useRef<Container | null>(null);
        const measurementPixi = useMemo(
            () => coordinates.worldToScene({ x: item.x, y: item.y }),
            [coordinates, item.x, item.y],
        );
        const draw = useCallback((graphics: Graphics) => {
            graphics.clear();
            graphics.circle(0, 0, MEASUREMENT_RADIUS);
            graphics.fill(ACTIVE_INTERACTION_COLOR);
        }, []);

        useConstantScreenSize(containerRef, MEASUREMENT_SCALE_LIMITS);

        return (
            <pixiContainer
                ref={containerRef}
                x={measurementPixi.x}
                y={measurementPixi.y}
                eventMode="none"
            >
                <pixiGraphics eventMode="none" draw={draw} />
            </pixiContainer>
        );
    },
);

MeasurementSelectionOverlay.displayName = "MeasurementSelectionOverlay";

export default memo(MeasurementObject, (prev, next) => {
    return (
        prev.item === next.item &&
        prev.layerId === next.layerId &&
        prev.onItemClick === next.onItemClick &&
        prev.onItemHover === next.onItemHover &&
        prev.onItemLeave === next.onItemLeave
    );
});
